import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(() => (sessionId ? { status: "polling", attempts: 0 } : { status: "error", message: "Session manquante" }));

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    const poll = async (n = 0) => {
      if (cancelled) return;
      if (n >= 8) { setState({ status: "timeout" }); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (data.upload_token) {
            // sessionStorage = scope to this tab/session, less XSS surface than localStorage
            sessionStorage.setItem("fv_upload_token", data.upload_token);
          }
          setState({ status: "paid", data });
        } else if (data.status === "expired") {
          setState({ status: "expired" });
        } else {
          setState({ status: "polling", attempts: n + 1 });
          setTimeout(() => poll(n + 1), 2000);
        }
      } catch {
        setTimeout(() => poll(n + 1), 2500);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {state.status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-[color:var(--accent)]" />
          <h1 className="font-display text-4xl mt-6">Validation du paiement...</h1>
          <p className="text-[color:var(--text-muted)] mt-2">Tentative {state.attempts + 1} / 8</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 mx-auto text-[color:var(--accent)]" />
          <h1 className="font-display text-5xl mt-6">Paiement confirmé !</h1>
          <p className="text-[color:var(--text-muted)] mt-3">{state.data.label} · {state.data.amount} {state.data.currency.toUpperCase()}</p>
          <p className="mt-4">Bienvenue dans le réseau Fedevo 2.0. Un email de confirmation arrive dans votre boîte.</p>
          {state.data.upload_token && (
            <div className="mt-6 glass rounded-xl p-4 text-left space-y-2" data-testid="payment-upload-token">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent)]">Votre jeton d'upload</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs break-all bg-[color:var(--bg)] border border-[color:var(--border)] rounded-lg px-3 py-2">{state.data.upload_token}</code>
                <button
                  data-testid="payment-copy-token"
                  onClick={() => navigator.clipboard.writeText(state.data.upload_token).then(() => toast.success("Jeton copié")).catch(() => toast.error("Impossible de copier"))}
                  className="shrink-0 inline-flex items-center gap-1.5 border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] px-3 py-2 rounded-full text-[10px] uppercase tracking-widest transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" /> Copier
                </button>
              </div>
              <p className="text-xs text-[color:var(--text-muted)]">Conservez-le : il ouvre votre <Link to="/creator" className="text-[color:var(--accent)] underline">Espace créateur</Link> pour téléverser vos vidéos.</p>
            </div>
          )}
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            {state.data.upload_token && (
              <Link to="/creator" data-testid="payment-go-creator" className="inline-block bg-[color:var(--accent)] text-black font-semibold px-6 py-3 rounded-full text-xs uppercase tracking-widest">Ouvrir l'espace créateur</Link>
            )}
            <Link to="/" className="inline-block border border-[color:var(--border)] hover:border-[color:var(--accent)] px-6 py-3 rounded-full text-xs uppercase tracking-widest transition-colors">Retour à l'accueil</Link>
          </div>
        </>
      )}
      {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
        <>
          <XCircle className="w-16 h-16 mx-auto text-[color:var(--text-muted)]" />
          <h1 className="font-display text-4xl mt-6">{state.status === "timeout" ? "Vérification expirée" : "Paiement non finalisé"}</h1>
          <p className="text-[color:var(--text-muted)] mt-3">{state.message || "Réessayez ou contactez-nous si le problème persiste."}</p>
          <Link to="/membership" className="mt-8 inline-block border border-[color:var(--border)] hover:border-[color:var(--accent)] px-6 py-3 rounded-full text-xs uppercase tracking-widest transition-colors">Réessayer</Link>
        </>
      )}
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-cancel-page">
    <XCircle className="w-16 h-16 mx-auto text-[color:var(--text-muted)]" />
    <h1 className="font-display text-4xl mt-6">Paiement annulé</h1>
    <p className="text-[color:var(--text-muted)] mt-3">Aucun débit n'a été effectué. Vous pouvez retenter quand vous voulez.</p>
    <Link to="/membership" className="mt-8 inline-block bg-[color:var(--accent)] text-black font-semibold px-6 py-3 rounded-full text-xs uppercase tracking-widest">Retour aux offres</Link>
  </div>
);
